import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import {
  Autocomplete,
  Box,
  TextField,
  InputAdornment,
  styled,
  Paper,
  Grid,
  Button,
  CircularProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import ClearIcon from '@mui/icons-material/Clear';
import { TSearchItem, useSearchList } from './hooks/useSearchList';
import { useStylesSearch } from './hooks/useStyles';
import { toAbsoluteUrl } from '../../../_base';
import { actions as catalogAction } from '../../store/ducks/product-catalog.duck';
import { IAppState } from '../../store/rootDuck';
import { useModalCategory } from '../../../_base/layout/categories/categoryContext';

const LIMIT_INTERVAL = 1500;

const StyledPaper = styled(Paper)({
  border: '1px solid #c0ccc1',
  borderRadius: 22,
  width: 'calc(100% + 2px)',
  marginLeft: -1,
  marginTop: 3,
});

export const NewSearchField = () => {
  const { close: closeCategories } = useModalCategory();
  const classes = useStylesSearch();
  const divRef = useRef<HTMLDivElement>(null);
  const intl = useIntl();
  const navigate = useNavigate();
  const [focus, setFocus] = useState(false);
  const searchText = useSelector((state: IAppState) => state.productsCatalog.searchText) ?? '';
  const categoryIdActive = useSelector(
    (state: IAppState) => state.productsCatalog.categoryIdActive
  );
  const perPage = useSelector((state: IAppState) => state.productsCatalog.per_page) ?? 10;
  const dispatch = useDispatch();

  const fetchDebouncedSearch = debounce(() => {
    if (searchText.trim() && searchText.length > 2) {
      fetchSearch(searchText);
    }
  }, LIMIT_INTERVAL);

  useEffect(() => {
    fetchDebouncedSearch();
    return () => fetchDebouncedSearch.cancel();
  }, [searchText]);

  const [selectOption, setSelectOption] = useState<any>(null);

  const { fetchSearch, loadingSearch, productsSearch, clearSearch } = useSearchList();

  const navigateToList = (item: TSearchItem) => {
    closeCategories();
    if (item.product_type === 'category') {
      // navigate('/products/catalog', { state: { categoryId: item.id } });
      dispatch(catalogAction.setSearching(''));
      navigate('/products/catalog');
      dispatch(catalogAction.setCategoryIdActive(item.id));
      return;
    }
    navigate(`/viewproduct/${item.id}`, {
      state: {
        searchProduct: item.item,
      },
    });
  };

  return (
    <div className={classes.textFieldWrapper} ref={divRef}>
      <Box
        sx={{
          // border: 'solid 1px #c0ccc1',
          width: '100%',
          borderTopLeftRadius: 23,
          borderTopRightRadius: 23,
          borderBottomLeftRadius: focus ? 0 : 23,
          borderBottomRightRadius: focus ? 0 : 23,
          borderBottomColor: focus ? 'transparent' : '#c0ccc1',
        }}
      >
        <Autocomplete
          clearOnBlur={false}
          open={searchText.length > 2 && focus}
          options={productsSearch || []}
          onChange={(_event, newValue) => {
            // dispatch(catalogAction.setSearchText(newValue.name));
            navigateToList(newValue);
          }}
          inputValue={searchText}
          value={selectOption}
          // value={selectOption || ''}
          style={{ width: '100%', padding: 0, color: '#fff' }}
          noOptionsText={intl.formatMessage({ id: 'CATEGORIES.TABLE.EMPTY' })}
          // loadingText={intl.formatMessage({ id: 'COMMON.LOADING' })}
          disableClearable
          getOptionLabel={option => option?.name}
          // loading={loadingSearch}
          disablePortal
          PaperComponent={props => (
            <>
              {!loadingSearch && productsSearch && (
                <StyledPaper {...props} style={{ width: divRef.current?.offsetWidth }}>
                  {props.children}
                </StyledPaper>
              )}
            </>
          )}
          onOpen={() => {
            setFocus(true);
            closeCategories();
          }}
          onClose={() => {
            setFocus(false);
          }}
          filterOptions={options => options}
          renderOption={(props, option) => (
            <Box
              {...props}
              component='li'
              sx={{
                padding: '0 22px',
                margin: 0,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
              key={option?.id}
              value={option?.id}
              onClick={(e: any) => {
                props.onClick && props.onClick(e);
                setSelectOption(option);
              }}
            >
              <Grid
                container
                justifyContent='space-between'
                wrap='nowrap'
                alignItems='center'
                gap={3}
              >
                <Grid item className={classes.title}>
                  {((searchText || searchText !== '') &&
                    option?.name?.toUpperCase()?.includes(searchText.toUpperCase())) ||
                  option?.item?.sku?.toUpperCase()?.includes(searchText.toUpperCase()) ? (
                    <>
                      {option?.name?.toUpperCase()?.includes(searchText.toUpperCase()) ? (
                        <>
                          <b className={classes.select}>
                            {option?.name?.toLowerCase()?.split(searchText.toLowerCase())[0] ||
                              ''}
                          </b>
                          <b className={classes.selectBold}>{searchText || ''}</b>
                          <b className={classes.select}>
                            {option?.name?.toLowerCase()?.split(searchText.toLowerCase())[1] ||
                              ''}
                          </b>
                        </>
                      ) : (
                        <>
                          <b className={classes.select}>
                            {option?.item?.sku
                              ?.toLowerCase()
                              ?.split(searchText.toLowerCase())[0] || ''}
                          </b>
                          <b className={classes.selectBold}>{searchText || ''}</b>
                          <b className={classes.select}>
                            {option?.item?.sku
                              ?.toLowerCase()
                              ?.split(searchText.toLowerCase())[1] || ''}
                          </b>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <b className={classes.select}> {option?.name} </b>
                      <b className={classes.selectBold}>{searchText || ''}</b>
                      {/* <b className={classes.selectName}>{option?.name}</b> */}
                    </>
                  )}
                </Grid>
                <Grid item className={classes.containerType}>
                  <b className={classes.selectType}>{option?.item?.category?.name || ''}</b>
                </Grid>
              </Grid>
            </Box>
          )}
          renderInput={params => (
            <TextField
              {...params}
              variant='outlined'
              size='small'
              className={classes.textField}
              onFocus={() => {
                closeCategories();
              }}
              // placeholder={intl.formatMessage({ id: 'PRODUCT.FILTER.SEARCH' })}
              value={searchText}
              onChange={e => {
                clearSearch();
                dispatch(catalogAction.setSearchText(e.target.value));
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  sessionStorage.setItem('productCatalogScrollPosition', '0');
                  navigate('/products/catalog');
                  dispatch(
                    catalogAction.fetchRequest({
                      page: 1,
                      perPage,
                      search: searchText,
                      categoryId: 0,
                    })
                  );
                  setFocus(false);
                }
              }}
              InputProps={{
                ...params.InputProps,
                style: {
                  height: 45,
                  color: '#01402e',
                  fontWeight: 500,
                  fontSize: 17,
                  padding: '0 10px',
                },
                startAdornment: (
                  <InputAdornment position='start' style={{ margin: 0 }}>
                    {!searchText.trim() ? (
                      <img
                        className={classes.search}
                        src={toAbsoluteUrl('/media/icons/searchIcon.svg')}
                        alt='Search'
                      />
                    ) : (
                      <ClearIcon
                        className={classes.cancel}
                        color='primary'
                        onClick={() => {
                          dispatch(catalogAction.setSearchText(''));
                          clearSearch();
                          setSelectOption(null);
                          dispatch(
                            catalogAction.fetchRequest({
                              page: 1,
                              perPage,
                              search: '',
                              categoryId: categoryIdActive || 0,
                            })
                          );
                        }}
                      />
                    )}
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position='end' style={{ margin: 0 }}>
                    {loadingSearch && <CircularProgress style={{ color: '#fff' }} size={20} />}
                    <Button
                      onClick={() => {
                        sessionStorage.setItem('productCatalogScrollPosition', '0');
                        navigate('/products/catalog');
                        dispatch(
                          catalogAction.fetchRequest({
                            page: 1,
                            perPage,
                            search: searchText,
                            categoryId: 0,
                          })
                        );
                        setFocus(false);
                      }}
                      className={classes.find}
                      style={{ textTransform: 'none' }}
                    >
                      {intl.formatMessage({ id: 'PRODUCT.FILTER.SEARCH' })}
                    </Button>
                  </InputAdornment>
                ),
              }}
              // eslint-disable-next-line react/jsx-no-duplicate-props
              inputProps={{
                ...params.inputProps,
                style: { color: '#fff' },
                autoComplete: 'off', // disable autocomplete and autofill
              }}
            />
          )}
        />
      </Box>
    </div>
  );
};
